import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Typography } from 'seagull';
import { fromRelationshipFundingSource } from '~/business-logic/paymentMethodCellLogic';
import { useAgreementRowMenuActions } from '~/components/AgreementActions/AgreementRowMenuActions';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, relationshipSummaryModel, timeModel } from '~/models';
import { agreementColumns, agreementMainColumn, agreementMobileFields, agreementSortColIndex, agreementSortName, agreementStatusIndicators, agreementStatusSkins, paymentMethodSubtitle, toAgreementRows } from './AgreementsDashboard.table.utils';
import { makeAgreementInlineActions } from './AgreementsTable.utils';
export function AgreementsTable(_0) {
    let { agreements, isVendor, onVerifyPaymentMethod, effectiveDateLabel, sort, onSortChange, changedAgreementIds, pagination, selection, testId } = _0, menuCallbacks = _object_without_properties(_0, [
        "agreements",
        "isVendor",
        "onVerifyPaymentMethod",
        "effectiveDateLabel",
        "sort",
        "onSortChange",
        "changedAgreementIds",
        "pagination",
        "selection",
        "testId"
    ]);
    const { WIP_AMENDMENTS } = useFeatureFlags();
    const { t: dashboardTexts } = useLocaleKeys('agreement.dashboard');
    const { t: columnHeadersTexts } = useLocaleKeys('agreement.dashboard.columnHeaders');
    const { t: rowTooltipTexts } = useLocaleKeys('agreement.dashboard.rowTooltips');
    const { t: rowActionTexts } = useLocaleKeys('agreement.dashboard.rowActions');
    const { t: statusLabelsTexts } = useLocaleKeys('agreement.filters.statusOptions');
    const { t: paymentMethodTexts } = useLocaleKeys('common.paymentMethod');
    const { t: timeTexts } = useLocaleKeys('common.time');
    const { getNow } = useIoContext();
    const navigate = useNavigate();
    const rowActionsOf = useAgreementRowMenuActions();
    const shape = {
        isVendor,
        showLastModifiedBy: WIP_AMENDMENTS === true
    };
    const agreementsById = useMemo(()=>new Map(agreements.map((agreement)=>[
                agreement.id,
                agreement
            ])), [
        agreements
    ]);
    const rowTexts = useMemo(()=>({
            paymentMethod: paymentMethodTexts,
            statusLabels: statusLabelsTexts,
            time: timeTexts
        }), []);
    const rows = useMemo(()=>toAgreementRows({
            agreements,
            isVendor,
            texts: rowTexts
        }), [
        agreements,
        isVendor,
        rowTexts
    ]);
    const statusSkins = useMemo(()=>agreementStatusSkins({
            agreements,
            texts: statusLabelsTexts
        }), [
        agreements
    ]);
    const columnMenu = useTableColumnMenu('agreements');
    const titleOf = (row)=>{
        const agreement = agreementsById.get(row.id);
        const title = agreement == null ? null : relationshipSummaryModel.displayedTitle(agreement, isVendor);
        return title == null ? undefined : [
            title
        ];
    };
    const mainColumn = agreementMainColumn({
        texts: columnHeadersTexts,
        subtitle: titleOf
    });
    const indicatorsOf = (row)=>{
        const agreement = agreementsById.get(row.id);
        return agreement == null ? [] : agreementStatusIndicators({
            agreement,
            showAmendmentDraft: WIP_AMENDMENTS === true && isVendor,
            texts: rowTooltipTexts
        });
    };
    const lastModifiedAgo = (row)=>{
        const record = relationshipSummaryModel.lastModificationRecord(agreementsById.get(row.id));
        return record == null ? undefined : [
            timeModel.formatTimeAgo(new Date(record.savedOn).getTime(), timeTexts, getNow().getTime())
        ];
    };
    const paymentSubtitle = (row)=>{
        const agreement = agreementsById.get(row.id);
        const subtitle = agreement == null ? undefined : paymentMethodSubtitle({
            agreement,
            dashboardTexts
        });
        return subtitle == null ? undefined : [
            subtitle
        ];
    };
    const pendingDetailsTooltip = (row)=>{
        const agreement = agreementsById.get(row.id);
        const isPending = agreement != null && fromRelationshipFundingSource(agreement.fundingSource).kind === 'pendingDetails';
        return isPending ? /*#__PURE__*/ _jsx(Typography, {
            children: paymentMethodTexts.pendingDetailsTooltip()
        }) : undefined;
    };
    const agreementLink = (agreement)=>isVendor || !relationshipModel.isPending(agreement) ? routes.agreementMinisiteRoutes(agreement.id).agreement : routes.clientOnboarding(agreement.id);
    const menuRowOf = (agreement)=>({
            id: agreement.id,
            title: agreement.title,
            draftId: agreement.draftId,
            isSample: agreement.isSample,
            customerName: relationshipSummaryModel.customerBusinessName(agreement),
            customerBusinessId: relationshipSummaryModel.customerBusinessId(agreement),
            status: agreement.status
        });
    const actionsFor = (agreement)=>rowActionsOf(_object_spread_props(_object_spread({}, menuCallbacks), {
            agreement: menuRowOf(agreement),
            isVendor,
            isTerminated: relationshipSummaryModel.isTerminated(agreement),
            heapId: agreement.isSample ? 'test-agreement' : 'agreement'
        }));
    const menuFor = (row)=>{
        const agreement = agreementsById.get(row.id);
        return agreement == null ? [] : actionsFor(agreement);
    };
    const inlineFor = makeAgreementInlineActions({
        agreementsById,
        isVendor,
        texts: {
            view: rowActionTexts.view(),
            addPaymentMethod: rowActionTexts.addPaymentMethod(),
            completeVerification: rowActionTexts.completeVerification()
        },
        onView: (agreement)=>navigate(agreementLink(agreement)),
        onAddPaymentMethod: (agreement)=>navigate(routes.agreementMinisiteRoutes(agreement.id).paymentMethods),
        onCompleteVerification: onVerifyPaymentMethod
    });
    const columns = agreementColumns({
        texts: columnHeadersTexts,
        shape,
        effectiveDateLabel,
        statusSkin: (status)=>{
            var _statusSkins_get;
            return (_statusSkins_get = statusSkins.get(status)) !== null && _statusSkins_get !== void 0 ? _statusSkins_get : 'default';
        },
        statusInfo: (status)=>status === statusLabelsTexts.completed() ? rowActionTexts.allServicesCompleted() : undefined,
        statusIndicators: indicatorsOf,
        lastModifiedSubtitle: lastModifiedAgo,
        paymentMethodInfo: pendingDetailsTooltip,
        paymentMethodSubtitleOf: paymentSubtitle
    });
    const tableSort = {
        name: agreementSortName({
            colIndex: sort.colIndex,
            shape
        }),
        direction: sort.sort === 'asc' ? 'asc' : 'desc'
    };
    return /*#__PURE__*/ _jsx(DataTable, {
        pagination: pagination,
        allowColumnResize: true,
        allowColumnReorder: true,
        reorderBi: {
            name: 'agreements_reorder_columns'
        },
        allowColumnHide: true,
        columnMenu: columnMenu,
        testId: testId,
        items: rows,
        getRowId: (row)=>row.id,
        mainColumn: mainColumn,
        columns: columns,
        mobile: agreementMobileFields({
            effectiveDateLabel,
            paymentMethodLabel: columnHeadersTexts.paymentMethod()
        }),
        rowHeapId: (row)=>{
            var _agreementsById_get;
            return ((_agreementsById_get = agreementsById.get(row.id)) === null || _agreementsById_get === void 0 ? void 0 : _agreementsById_get.isSample) ? 'test-agreement' : 'agreement';
        },
        onRowClick: (row)=>{
            const agreement = agreementsById.get(row.id);
            if (agreement) navigate(agreementLink(agreement));
        },
        sort: _object_spread_props(_object_spread({}, tableSort), {
            onChange: (next)=>onSortChange({
                    colIndex: agreementSortColIndex({
                        name: next.name,
                        shape
                    }),
                    sort: next.direction
                })
        }),
        isRowEntering: (row)=>changedAgreementIds.has(row.id),
        selection: isVendor ? selection : undefined,
        rowActions: {
            menuBi: {
                name: 'agreements_actions'
            },
            inline: inlineFor,
            menu: menuFor
        }
    });
}
